import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import environment from "../../../environments/environment";
import { MigrationService } from "../../services/migration.service";
import {
  Company,
  MigrationCreateResponse,
  MigrationRejectResponse,
  MigrationStatusResponse,
  Protocol,
  ProtocolsToMigrateResponse,
} from "../../interface/interfaces";
import { PaginatorFilter } from "@app/models/grids";
import { PageEvent } from "@angular/material/paginator";
import { DialogService } from "@app/services/dialog.service";
import GridsConfig from "@app/common/grid.config";
import { ApiService } from "@app/services/api.service";
import { MessageService } from "@app/services/message.service";
import { ToastService } from "@app/services/toast.service";
import { MigrationStatus } from "@app/common/constants";
import { RejectMigrationRequest } from "@app/interface/models";
import { MigrationStore } from "@app/services/migration.store";

@Component({
  selector: "app-company-migration",
  templateUrl: "./company-migration.component.html",
  styleUrls: ["./company-migration.component.scss"],
  standalone: false,
})
export class AppCompanyMigrationComponent implements OnInit {
  constructor(
    private http: HttpClient,
    private migrationService: MigrationService,
    private apiService: ApiService,
    private messageService: MessageService,
    private toastService: ToastService,
    private migrationStore: MigrationStore,
  ) {}
  @ViewChild("pushMigration", { read: ElementRef })
  pushMIgrationBtn!: ElementRef;

  title = "Migration";
  readonly apiUrl = environment.apiUrl;
  readonly environmentName = environment.environmentName;
  // ui indicators
  loading = true;
  loadingTable = false;
  showProtocols = false;
  shouldEnableMigrationCommands = false;

  // company
  company: Company | undefined;
  // Protocols table
  protocols: Protocol[] = [];
  protocolsPaginated: Protocol[] = [];
  protocolsColumns = GridsConfig.MigrationProtocol.Columns;
  protocolsTableHeaders = GridsConfig.MigrationProtocol.Headers;
  protocolsColumnWidths = GridsConfig.MigrationProtocol.Widths;
  columnsLink: string[] = environment.cnx5Url ? ["title"] : [];
  // paginator
  paginatorFilter: PaginatorFilter = new PaginatorFilter();

  ngOnInit() {
    this.paginatorFilter.defaultPageSize = 5;
    this.resetProtocolTablePagination();
  }

  companySelectionChanged(event: Company | null) {
    this.company = event || undefined;
    this.resetProtocolTablePagination();

    if (this.company?.company_id) {
      // one time setup for innitial page load
      if (!this.showProtocols) {
        this.showProtocols = true;
      }
      // set migration buttons enable status
      this.shouldEnableMigrationCommands =
        this.company?.status === null ||
        this.company.status == MigrationStatus.fixed;

      this.loadStateChange(true, false, undefined, true);

      this.apiService.getCompanyProtocols(this.company.company_id).subscribe({
        next: (data: ProtocolsToMigrateResponse) => {
          this.protocols = this.migrationService.processProtocolsData(
            data.protocols,
          );

          if (this.company) {
            this.company.has_irbnet_sites = this.protocols.some(
              (element) => element.has_irbnet_sites === true,
            );
          }

          this.addRemoveIrbNetColumn(this.company?.has_irbnet_sites ?? false);

          this.shouldEnableMigrationCommands =
            this.shouldEnableMigrationCommands && this.protocols.length > 0;

          this.applyProtocolPagination();
          this.loadStateChange(false, false, undefined, false);
        },
        error: (error) => {
          this.protocols = [];
          this.applyProtocolPagination();
          this.loadStateChange(false, false, undefined, false);
        },
      });
    }
    console.log(event);
  }

  addRemoveIrbNetColumn(add: boolean) {
    this.protocolsColumns = this.protocolsColumns.filter(
      (element) => element != "has_irbnet_sites",
    );
    this.protocolsTableHeaders = this.protocolsTableHeaders.filter(
      (element) => element != "IrbNet",
    );
    if (add) {
      this.protocolsColumns.push("has_irbnet_sites");
      this.protocolsTableHeaders.push("IrbNet");
    }
  }
  protocolCellClicked($event: any) {
    if (this.columnsLink.length === 0) return;
    if ($event.column == "title") {
      let cnx5ExternalReferenceId = $event.element.cnx5ExternalReferenceId;
      const url = `${environment.cnx5Url}/studies/STU-${cnx5ExternalReferenceId}/sites`;
      window.open(url, "_blank");
    }
  }
  paginateProtocols(event: PageEvent) {
    this.paginatorFilter.currentPage = event.pageIndex;
    this.paginatorFilter.pageSize = event.pageSize;
    this.applyProtocolPagination();
  }
  resetProtocolTablePagination() {
    this.paginatorFilter.currentPage = 0;
    this.paginatorFilter.pageSize = this.paginatorFilter.defaultPageSize || 5;
  }

  // ************* Publish migration *************
  publishMigrationClicked() {
    // check first if there is already a migration in progress
    if (!this.company?.company_id) return;
    this.loadStateChange(true);
    this.apiService.getMigrationByCompany(this.company?.company_id).subscribe({
      next: (data: MigrationStatusResponse) => {
        this.loadStateChange(false);
        if (!data.in_progress) {
          this.migrationService.publishMigrationModal(
            this.company?.company_name,
            () => this.publishMigration(),
            () => console.log("User cancelled publish migration"),
          );
        } else {
          this.toastService.warning(
            `Migration already in progress for company ${this.company?.company_name}!`,
            this.pushMIgrationBtn.nativeElement,
          );
        }
      },
      error: (error) => {
        this.loadStateChange(false);
      },
    });
  }

  private publishMigration() {
    if (this.company?.company_id) {
      this.loading = true;
      this.apiService.pushCompany(this.company?.company_id).subscribe({
        next: (data: MigrationCreateResponse) => {
          this.shouldEnableMigrationCommands = false;
          this.loadStateChange(
            false,
            true,
            `Migration with id ${data.id} created`,
          );
        },
        error: (error) => {
          this.loading = false;
        },
      });
    }
  }

  // ************* Reject migration *************
  rejectMigrationClicked() {
    this.migrationService.rejectShowMigrationModal(
      this.company?.company_name,
      (comments) => this.rejectMigration(comments),
      () => console.log("User cancelled rejected migration"),
    );
  }

  private rejectMigration(comments?: string) {
    if (!this.company?.company_id) return;

    if (!comments || comments.trim() === "") {
      this.toastService.error("Comments are required for rejection");
      return;
    }

    this.loadStateChange(true);
    this.shouldEnableMigrationCommands = false;

    this.apiService
      .rejectCompanyMigration(
        this.company?.company_id,
        new RejectMigrationRequest(comments),
      )
      .subscribe({
        next: (data: MigrationRejectResponse) => {
          this.loadStateChange(false, true, `${data.message}`);
        },
        error: (error) => {
          this.loadStateChange(false);
        },
      });
  }

  // ******** PRIVATE ********
  private loadStateChange(
    loading: boolean,
    sendReloadEvent: boolean = false,
    messageText?: string,
    loadingTable: boolean = false,
  ) {
    this.loading = loading;
    this.migrationStore.setLoading(loading);
    this.loadingTable = loadingTable;

    if (sendReloadEvent) {
      this.messageService.sendReloadMessage(true);
    }
    if (messageText) {
      this.toastService.success(messageText);
    }
  }

  private applyProtocolPagination() {
    const skipRecords: number =
      this.paginatorFilter.currentPage * this.paginatorFilter.pageSize;
    this.protocolsPaginated = this.protocols
      .slice(skipRecords)
      .slice(0, this.paginatorFilter.pageSize);
    this.paginatorFilter.count = this.protocols.length;
  }
}
